/**
 * Firebase Configuration
 * Initializes Firebase only in live (app) mode, not in demo mode
 * 
 * Environment variables are loaded from .env.{mode} files:
 *   .env.development   → skybolt-dev (npm run dev)
 *   .env.staging       → skybolt-99adb (npm run stage)
 *   .env.production    → skybolt-99adb (npm run build)
 *   .env.test          → skybolt-dev (npm run test)
 */

import { requireEnv } from './validate.js';

// Validate required Firebase env vars at module load
requireEnv('Firebase', [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_DATABASE_URL',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
]);

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL:       import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
};

// Firebase instances (initialized only in app mode)
let app = null;
let auth = null;
let db = null;
let rtdb = null;
let storage = null;

/**
 * Initialize Firebase
 * Should only be called in app/live mode, not demo mode
 */
export async function initFirebase() {
  // Prevent double initialization
  if (app) {
    console.warn('Firebase already initialized');
    return { app, auth, db, rtdb, storage };
  }

  try {
    // Import Firebase modules dynamically (only loaded when needed)
    const { initializeApp } = await import('firebase/app');
    const { getAuth } = await import('firebase/auth');
    const { getFirestore } = await import('firebase/firestore');
    const { getDatabase } = await import('firebase/database');
    const { getStorage } = await import('firebase/storage');

    // Initialize Firebase app
    app = initializeApp(firebaseConfig);

    // Initialize Auth
    auth = getAuth(app);

    // Set browser persistence (skip in test/node environments where it's unavailable)
    if (import.meta.env.MODE !== 'test') {
      const { setPersistence, browserLocalPersistence } = await import('firebase/auth');
      await setPersistence(auth, browserLocalPersistence);
    }

    // Initialize Firestore (basic initialization without offline persistence for now)
    db = getFirestore(app);

    // Initialize Realtime Database (for legacy talent/media data)
    rtdb = getDatabase(app);

    // Initialize Storage
    storage = getStorage(app);

    return { app, auth, db, rtdb, storage };
  } catch (error) {
    console.error('✗ Firebase initialization failed:', error);
    throw error;
  }
}

// Keep track of user data listener for cleanup
let userDataUnsubscribe = null;

/**
 * Set up Firebase auth state listener
 * Called once during app initialization (after initFirebase)
 * 
 * When Firebase detects a persisted session:
 * 1. Subscribes to user data from RTDB (real-time updates)
 * 2. Dispatches login action with user data on changes
 * 3. This updates stores and triggers navigation if needed
 */
export async function setupAuthStateListener() {
  const { auth, rtdb } = getFirebase();
  const { onAuthStateChanged } = await import('firebase/auth');
  const { ref, onValue } = await import('firebase/database');
  const { login } = await import('../actions/auth/login.js');
  
  return new Promise((resolve) => {
    let isFirstCheck = true;
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Clean up existing user data listener if any
          if (userDataUnsubscribe) {
            userDataUnsubscribe();
            userDataUnsubscribe = null;
          }
          
          // Subscribe to user data changes in Realtime Database
          const userRef = ref(rtdb, `users/${firebaseUser.uid}`);
          
          userDataUnsubscribe = onValue(userRef, (snapshot) => {
            if (snapshot.exists()) {
              const userData = snapshot.val();
              
              // Dispatch login action to update stores
              // Pass userData directly so the effect can set context.data
              // The effect will recognize this as a restored session
              // Include Firebase UID as id
              login({ _restored: true, _userData: { ...userData, id: firebaseUser.uid } });
              
              // Resolve on first auth check after user data is loaded
              if (isFirstCheck) {
                isFirstCheck = false;
                resolve(unsubscribe);
              }
            } else {
              console.error('✗ User document not found in RTDB');
              
              // Resolve even if user data not found (prevents hanging)
              if (isFirstCheck) {
                isFirstCheck = false;
                resolve(unsubscribe);
              }
            }
          }, (error) => {
            console.error('✗ Error subscribing to user data:', error);
            
            // Resolve on error to prevent hanging
            if (isFirstCheck) {
              isFirstCheck = false;
              resolve(unsubscribe);
            }
          });
        } catch (error) {
          console.error('✗ Error restoring auth state:', error);
          
          // Resolve on error to prevent hanging
          if (isFirstCheck) {
            isFirstCheck = false;
            resolve(unsubscribe);
          }
        }
      } else {
        console.log('No persisted auth session found');
        
        // Clean up user data listener on sign out
        if (userDataUnsubscribe) {
          userDataUnsubscribe();
          userDataUnsubscribe = null;
        }
        
        // Resolve after first check (no user logged in)
        if (isFirstCheck) {
          isFirstCheck = false;
          resolve(unsubscribe);
        }
      }
    });
  });
}

/**
 * Get Firebase instances
 * Throws error if Firebase not initialized (helps catch demo mode leaks)
 */
export function getFirebase() {
  if (!app) {
    throw new Error('Firebase not initialized. Are you in demo mode?');
  }
  return { app, auth, db, rtdb, storage };
}

/**
 * @internal — for effect integration tests only.
 * Injects externally-created Firebase instances so getFirebase() works
 * without calling initFirebase() (which requires a browser environment).
 */
export function setFirebase(instances) {
  app = instances.app;
  db = instances.db;
  rtdb = instances.rtdb;
  auth = instances.auth;
  storage = instances.storage || null;
}

/**
 * @internal — for effect integration test teardown only.
 * Clears the module-level singletons. Does NOT call deleteApp.
 */
export function resetFirebase() {
  app = null;
  db = null;
  rtdb = null;
  auth = null;
  storage = null;
}

/**
 * Get Firebase Storage SDK methods
 * Wraps dynamic import to centralize Firebase dependencies
 */
export async function getStorageUtils() {
  return import('firebase/storage');
}

/**
 * Check if Firebase is initialized
 */
export function isFirebaseInitialized() {
  return app !== null;
}

// Export configuration for reference
export { firebaseConfig };

// ============================================================================
// Firestore Subscription Management
// ============================================================================

/**
 * Global registry of active Firestore subscriptions
 * 
 * Provides centralized tracking for all real-time listeners to ensure
 * proper cleanup on logout and prevent memory leaks.
 * 
 * Key format examples: 
 *   - 'projects/proj_123' (single document)
 *   - 'projects/byOffice/office_456' (collection query)
 *   - 'calls/open' (collection group query)
 *   - 'events/byCasting/cast_789' (filtered collection)
 * 
 * Usage in effects:
 * 
 *   import { registerSubscription, getSubscription } from '../../config/firebase.js';
 *   
 *   const key = `projects/${projectId}`;
 *   const existing = getSubscription(key);
 *   if (existing) {
 *     // Already subscribed - reuse pending data if available
 *     if (existing.pendingData) {
 *       context.data = existing.pendingData;
 *       existing.pendingData = null;
 *     }
 *     return;
 *   }
 *   
 *   const subscription = { unsubscribe: null, pendingData: null };
 *   registerSubscription(key, subscription);
 *   
 *   // ... set up Firestore subscription
 *   subscription.unsubscribe = onSnapshot(...);
 * 
 * Cleanup on logout:
 * 
 *   import { unsubscribeAll } from '../config/firebase.js';
 *   
 *   before(logout, () => {
 *     unsubscribeAll();
 *   });
 */

// Global registry of active subscriptions
// Key format examples: 'projects/proj_123', 'packages/pkg_456', 'calls/open'
const subscriptions = new Map();

/**
 * Register a new Firestore subscription
 * 
 * @param {string} key - Unique identifier for the subscription
 * @param {object} subscription - Subscription object with unsubscribe and pendingData properties
 * @throws {Error} If Firebase is not initialized or user is not authenticated
 */
export function registerSubscription(key, subscription) {
  // Safety check: prevent subscriptions when Firebase isn't initialized
  if (!isFirebaseInitialized()) {
    throw new Error(`[Subscriptions] Cannot register subscription '${key}': Firebase not initialized`);
  }
  
  // Safety check: prevent subscriptions when user is not authenticated
  if (auth && !auth.currentUser) {
    console.warn(`[Subscriptions] Cannot register subscription '${key}': No authenticated user`);
    throw new Error(`Cannot register subscription: No authenticated user`);
  }
  
  if (subscriptions.has(key)) {
    console.warn(`[Subscriptions] Subscription already exists for key: ${key}`);
  }
  subscriptions.set(key, subscription);
  console.log(`[Subscriptions] Registered: ${key} (total: ${subscriptions.size})`);
}

/**
 * Get an existing Firestore subscription
 * 
 * @param {string} key - Unique identifier for the subscription
 * @returns {object|null} Subscription object or null if not found
 */
export function getSubscription(key) {
  return subscriptions.get(key) || null;
}

/**
 * Check if a Firestore subscription exists
 * 
 * @param {string} key - Unique identifier for the subscription
 * @returns {boolean} True if subscription exists
 */
export function hasSubscription(key) {
  return subscriptions.has(key);
}

/**
 * Unsubscribe from a specific Firestore subscription
 * 
 * @param {string} key - Unique identifier for the subscription
 */
export function unsubscribe(key) {
  const subscription = subscriptions.get(key);
  if (subscription && subscription.unsubscribe) {
    try {
      subscription.unsubscribe();
      console.log(`[Subscriptions] Unsubscribed: ${key}`);
    } catch (error) {
      console.error(`[Subscriptions] Error unsubscribing from ${key}:`, error);
    }
  }
  subscriptions.delete(key);
}

/**
 * Unsubscribe from all active Firestore subscriptions
 * 
 * Called during logout to clean up all Firestore listeners
 * and prevent permission errors.
 */
export function unsubscribeAll() {
  console.log(`[Subscriptions] Unsubscribing from ${subscriptions.size} active subscriptions...`);
  
  const errors = [];
  
  subscriptions.forEach((subscription, key) => {
    if (subscription && subscription.unsubscribe) {
      try {
        subscription.unsubscribe();
        console.log(`[Subscriptions] ✓ Unsubscribed: ${key}`);
      } catch (error) {
        console.error(`[Subscriptions] ✗ Error unsubscribing from ${key}:`, error);
        errors.push({ key, error });
      }
    }
  });
  
  // Clear the registry
  subscriptions.clear();
  
  console.log(`[Subscriptions] Cleanup complete. ${errors.length > 0 ? `${errors.length} errors occurred.` : 'All subscriptions cleaned up successfully.'}`);
  
  if (errors.length > 0) {
    console.warn('[Subscriptions] Errors during cleanup:', errors);
  }
}

/**
 * Get count of active Firestore subscriptions (for debugging)
 * 
 * @returns {number} Number of active subscriptions
 */
export function getSubscriptionCount() {
  return subscriptions.size;
}

/**
 * Get all subscription keys (for debugging)
 * 
 * @returns {string[]} Array of subscription keys
 */
export function getSubscriptionKeys() {
  return Array.from(subscriptions.keys());
}
